import { CustomDropdown, fieldSet, getCurrentStatus, clientApi } from "shared"
import { useEffect, useState } from "react"
import "./AccountForm.css"
import type { ClientProps } from "Utils/client.types"

interface PrevStateProps {
	clientId: string
	credits?: {
		monthly: string
		annually: number
	}
	billingDate?: string
	paymentType?: string
	productType?: string
	status?: string
	freeTrialEnd?: string
	subdomain: string
	cuCode?: string
}

export function AccountForm({
	data,
	clientId,
	setData,
}: Readonly<{
	data: ClientProps
	clientId: string
	setData: (data: ClientProps) => void
}>) {
	const [freeTrial, setFreeTrial] = useState(false)
	const [formField, setFormField] = useState<PrevStateProps>({
		clientId: clientId,
		subdomain: data?.subdomain,
		cuCode: data?.cuCode ?? "",
	})
	const [changeTrigger, setChangeTrigger] = useState(false)

	const canEditCuCode = !data?.evoEnabled || (data?.cuCode ?? "").length === 0

	const onSubmit = (body) => {
		if (body?.subdomain != null && body?.subdomain !== data?.subdomain) {
			//subdomain needs special handling
			if (
				window.confirm(
					"Changing the subdomain will invalidate any landing pages created with the old version. Are you sure you want to proceed?"
				)
			) {
				clientApi.updateSubdomain({ clientId, subdomain: body.subdomain })
			} else {
				body.subdomain = data.subdomain
			}
		}
		clientApi.patch({ clientId, updates: body })
		setChangeTrigger(false)
		setData({ ...data, ...body })
	}

	useEffect(() => {
		if (changeTrigger) {
			const body = {
				...formField,
			}
			onSubmit(body)
		}
	}, [formField])

	const onChange = (event) => {
		setFormField((prevState: PrevStateProps) => {
			if (event.target.id === "credits") {
				return {
					...prevState,
					[event.target.id]: {
						monthly: parseInt(event.target.value),
						annually: parseInt(event.target.value) * 12,
					},
				}
			}
			if (event.target.id === "status") {
				return {
					...prevState,
					[event.target.id]: event.target.value,
				}
			}
			return {
				...prevState,
				[event.target.id]: event.target.value,
			}
		})
	}

	useEffect(() => {
		const statusCheck = "client_status#free_trial"
		if (data?.miscData === statusCheck || formField?.status === statusCheck) {
			setFreeTrial(true)
		} else {
			setFreeTrial(false)
		}
	}, [data?.miscData, formField?.status])

	return (
		<form onSubmit={onSubmit}>
			<div className="account-form-container">
				<div className="row-one">
					<label className="form-label" htmlFor="credits">
						Monthly Credits:
					</label>
				</div>
				<div className="row-two">
					<input
						id="credits"
						type="text"
						inputMode="numeric"
						onChange={onChange}
						value={formField?.credits?.monthly ?? data?.credits?.monthly}
						onBlur={() => onSubmit(formField)}
					/>
				</div>
			</div>
			<div className="account-form-container">
				<div className="row-one">
					<label htmlFor="billingDate">Billing Start Date:</label>
				</div>
				<div className="row-two">
					<input
						id="billingDate"
						className="account-form-date"
						type="date"
						onBlur={(e) => {
							//  Note: This is a known issue with the date picker component.
							// TODO: Create a custom date picker component.

							onChange({
								target: {
									id: "billingDate",
									value: e.target.value,
								},
							})
							setChangeTrigger(true)
						}}
						onChange={(e) => {
							onChange({
								target: {
									id: "billingDate",
									value: e.target.value,
								},
							})
						}}
						value={formField?.billingDate || data?.billingDate || ""}
					/>
				</div>
			</div>
			<div className="account-form-container">
				<div className="row-one">
					<label className="form-label label" htmlFor="paymentType">
						Payment Type:
					</label>
				</div>
				<div className="row-two">
					<CustomDropdown
						actions={fieldSet.paymentType}
						bestOption="-"
						onChange={onChange}
						formId="paymentType"
						value={data?.paymentType || formField?.paymentType}
						setChangeTrigger={setChangeTrigger}
					/>
				</div>
			</div>
			<div className="account-form-container">
				<div className="row-one">
					<label className="form-label label" htmlFor="productType">
						Product Type:
					</label>
				</div>
				<div className="row-two">
					<CustomDropdown
						actions={fieldSet.productType}
						bestOption="-"
						onChange={onChange}
						formId="productType"
						value={data?.productType || formField?.productType}
						setChangeTrigger={setChangeTrigger}
					/>
				</div>
			</div>
			<div className="account-form-container">
				<div className="row-one">
					<label className="form-label" htmlFor="status">
						Status:
					</label>
				</div>
				<div className="row-two">
					<CustomDropdown
						actions={fieldSet?.status}
						bestOption="Free Trial"
						onChange={onChange}
						formId="status"
						value={getCurrentStatus(data?.miscData) || formField?.status}
						setChangeTrigger={setChangeTrigger}
					/>
				</div>
				<div>
					{freeTrial ? (
						<div className="row-three">
							<label
								htmlFor="freeTrialEnd"
								style={{
									paddingRight: "10px",
								}}>
								Free Trial End
							</label>
							<input
								id="freeTrialEnd"
								type="date"
								className="account-form-date"
								onBlur={(e) => {
									//  Note: This is a known issue with the date picker component.
									onChange({
										target: {
											id: "freeTrialEnd",
											value: e.target.value,
										},
									})
									setChangeTrigger(true)
								}}
								onChange={(e) =>
									onChange({
										target: { id: "freeTrialEnd", value: e.target.value },
									})
								}
								value={formField?.freeTrialEnd || data?.freeTrialEnd || ""}
							/>
						</div>
					) : null}
				</div>
			</div>
			<div className="account-form-container">
				<div className="row-one">
					<label htmlFor="subdomain" className="form-label">
						Subdomain:
					</label>
				</div>
				<div className="row-two">
					<input
						id="subdomain"
						type="text"
						onChange={onChange}
						value={formField?.subdomain ?? data?.subdomain}
						onBlur={() => onSubmit(formField)}
					/>
				</div>
			</div>
			<div className="account-form-container">
				<div className="row-one">
					<label htmlFor="cuCode" className="form-label">
						Salesforce Account Number (CuCode):
					</label>
				</div>
				<div className="row-two">
					<input
						disabled={!canEditCuCode}
						id="cuCode"
						type="text"
						onChange={onChange}
						value={formField?.cuCode ?? data?.cuCode}
						onBlur={() => onSubmit(formField)}
					/>
				</div>
			</div>
		</form>
	)
}
